import React, {createContext, useContext, useReducer, useCallback, useState, useRef} from 'react';
import EventEmitter from 'events'; // Or your preferred event emitter

// Initial state
const initialState = {
    state: 'idle'
};

// Action types
const PLAY = 'PLAY';
const PAUSE = 'PAUSE';
const RESET = 'RESET';
const FINISHED = 'FINISHED';

// Reducer function
function playbackReducer(state, action) {
    switch (action.type) {
        case PLAY:
            return {
                ...state,
                state: 'playing',
            };
        case PAUSE:
            return {
                ...state,
                state: 'paused',
            };
        case RESET:
            return {
                ...state,
                state: 'idle',
            };
        case FINISHED:
            return {
                ...state,
                state: 'idle',
            };
        default:
            return state;
    }
}

// Create context
const LessonPlaybackStateContext = createContext();

// Create provider component
function LessonPlaybackProvider({ children }) {
    const [state, dispatch] = useReducer(playbackReducer, initialState, () => initialState);
    const eventEmitter = useRef(new EventEmitter());

    /* Track player as a useRef to avoiding play audio when the player was set as vimeo. The useRef allow the value to
    * change and take effect in the current react rendering, rather than wait for a new rendering
    * */
    const player = useRef('audio');

    const play = useCallback(() => {
        dispatch({ type: PLAY, payload: { } });
        eventEmitter.current.emit('play', player.current);
    }, []);

    const pause = useCallback(() => {
        dispatch({ type: PAUSE, payload: { } });
        eventEmitter.current.emit('pause', player.current);
    }, []);

    const togglePlayback = useCallback(() => {
        if (state.state === 'playing') {
            pause();
        } else {
            play();
        }
    }, [state]);

    const reset = useCallback(() => {
        dispatch({ type: RESET, payload: { } });
        eventEmitter.current.emit('stop', player.current);
    }, []);

    const finished = useCallback(() => {
        dispatch({ type: FINISHED, payload: { } });
        eventEmitter.current.emit('finished', player.current);
    }, []);

    const setPlayer = useCallback((newPlayer) => {
        console.log(`Setting player to ${newPlayer}`)
        player.current = newPlayer;
        eventEmitter.current.emit('playerChanged', newPlayer);
    }, []);

    const on = useCallback((event, listener, expectedPlayer) => {
        eventEmitter.current.on(event, (eventPlayer) => {
            if (player.current === undefined || expectedPlayer === undefined || player.current === expectedPlayer) {
                listener(eventPlayer);
            }
        });
    }, [eventEmitter]);

    const off = useCallback((event, listener) => {
        eventEmitter.current.off(event, listener);
    }, [eventEmitter]);

    const value = {
        state: state.state,
        play,
        pause,
        togglePlayback,
        reset,
        finished,
        setPlayer,
        on,
        off,
    };

    return (
        <LessonPlaybackStateContext.Provider value={value}>
            {children}
        </LessonPlaybackStateContext.Provider>
    );
}

export { LessonPlaybackProvider, LessonPlaybackStateContext };